'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { supabase } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BusinessPassport {
  legal_name: string;
  trade_name: string;
  entity_type: string;
  pan: string | null;
  gstin: string | null;
  founders: Array<{ full_name: string; email: string; pan: string; is_director: boolean }>;
  registered_offices: Array<{ address_line1: string; address_line2: string; city: string; state: string; pincode: string }>;
  business_activities: Array<{ description: string; nic_code: string; industry: string }>;
  business_passports: { completeness_score: number; msme_number: string | null } | null;
  setup_progress: number;
}

interface EligibilityResult {
  eligible: boolean;
  mandatoryRegistration: boolean;
  reason: string;
  missingFields: string[];
  score: number;
  turnoverThreshold: string;
  recommendation: string;
}

interface GSTFormData {
  // Business details
  legalName: string;
  tradeName: string;
  pan: string;
  entityType: string;
  // Address
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  // Contact
  primaryEmail: string;
  primaryMobile: string;
  // Business activity
  businessNature: string;
  nicCode: string;
  // Turnover
  annualTurnover: string;
  supplyType: string;
  // Authorized signatory
  signatoryName: string;
  signatoryPan: string;
  signatoryDesignation: string;
}

interface AIGuidance {
  field: string;
  suggestion: string;
  confidence: 'high' | 'medium' | 'low';
}

// ─── Demo / fallback data ─────────────────────────────────────────────────────

const demoPassport: BusinessPassport = {
  legal_name: 'Elysium Fragrances Private Limited',
  trade_name: 'Elysium',
  entity_type: 'private_limited',
  pan: 'AABCE1234F',
  gstin: null,
  founders: [
    { full_name: 'Arjun Sharma', email: 'arjun@elysiumfragrances.com', pan: 'ABCPS1234D', is_director: true },
    { full_name: 'Priya Mehta', email: 'priya@elysiumfragrances.com', pan: 'XYZPM5678E', is_director: true },
  ],
  registered_offices: [{ address_line1: '42, Sector 18', address_line2: 'Noida Industrial Area', city: 'Noida', state: 'Uttar Pradesh', pincode: '201301' }],
  business_activities: [{ description: 'Manufacture and sale of perfumes, cosmetics, and skincare products', nic_code: '20231', industry: 'Manufacturing - Cosmetics' }],
  business_passports: { completeness_score: 72, msme_number: null },
  setup_progress: 78,
};

const entityTypeLabels: Record<string, string> = {
  private_limited: 'Private Limited Company',
  llp: 'Limited Liability Partnership',
  opc: 'One Person Company',
  partnership: 'Partnership Firm',
  sole_proprietorship: 'Sole Proprietorship',
};

const stateGSTCodes: Record<string, string> = {
  'Andhra Pradesh': '37', 'Arunachal Pradesh': '12', 'Assam': '18', 'Bihar': '10',
  'Chhattisgarh': '22', 'Goa': '30', 'Gujarat': '24', 'Haryana': '06',
  'Himachal Pradesh': '02', 'Jharkhand': '20', 'Karnataka': '29', 'Kerala': '32',
  'Madhya Pradesh': '23', 'Maharashtra': '27', 'Manipur': '14', 'Meghalaya': '17',
  'Mizoram': '15', 'Nagaland': '13', 'Odisha': '21', 'Punjab': '03',
  'Rajasthan': '08', 'Sikkim': '11', 'Tamil Nadu': '33', 'Telangana': '36',
  'Tripura': '16', 'Uttar Pradesh': '09', 'Uttarakhand': '05', 'West Bengal': '19',
  'Delhi': '07', 'Jammu & Kashmir': '01', 'Ladakh': '38',
};

const feeStructure = [
  { category: 'Government Filing Fee', amount: '₹0', note: 'GST registration is free of charge', type: 'free' },
  { category: 'DSC (Digital Signature Certificate)', amount: '₹1,000 – ₹2,500', note: 'Required for companies & LLPs. One-time cost.', type: 'required' },
  { category: 'Professional Assistance', amount: '₹1,500 – ₹5,000', note: 'CA / CS fee for filing assistance (optional)', type: 'optional' },
  { category: 'GST Practitioner', amount: '₹500 – ₹2,000', note: 'Authorized GST practitioner fee (optional)', type: 'optional' },
];

const requiredDocuments = [
  { doc: 'PAN Card of Business', who: 'All entity types', status: 'required' },
  { doc: 'Aadhaar Card of Authorized Signatory', who: 'All entity types', status: 'required' },
  { doc: 'Proof of Business Registration', who: 'Companies, LLPs', status: 'required' },
  { doc: 'Address Proof of Principal Place of Business', who: 'All entity types', status: 'required' },
  { doc: 'Bank Account Statement / Cancelled Cheque', who: 'All entity types', status: 'required' },
  { doc: 'Digital Signature Certificate (DSC)', who: 'Companies, LLPs', status: 'conditional' },
  { doc: 'Letter of Authorization / Board Resolution', who: 'Companies, LLPs', status: 'conditional' },
  { doc: 'Partnership Deed', who: 'Partnership Firms', status: 'conditional' },
];

const workflowSteps = [
  { id: 1, label: 'Eligibility Check', icon: '◎' },
  { id: 2, label: 'Business Details', icon: '◈' },
  { id: 3, label: 'Address & Contact', icon: '◉' },
  { id: 4, label: 'Business Activity', icon: '◇' },
  { id: 5, label: 'Signatory', icon: '✓' },
  { id: 6, label: 'Review & Submit', icon: '⬡' },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function GSTPage() {
  const [passport, setPassport] = useState<BusinessPassport>(demoPassport);
  const [step, setStep] = useState(1);
  const [eligibility, setEligibility] = useState<EligibilityResult | null>(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [aiGuidance, setAiGuidance] = useState<AIGuidance[]>([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [activeTab, setActiveTab] = useState<'form' | 'fees' | 'docs'>('form');
  const [submitted, setSubmitted] = useState(false);
  const [turnoverInput, setTurnoverInput] = useState('');
  const [supplyType, setSupplyType] = useState('goods_services');
  const [interState, setInterState] = useState(false);

  const [form, setForm] = useState<GSTFormData>({
    legalName: '', tradeName: '', pan: '', entityType: '',
    addressLine1: '', addressLine2: '', city: '', state: '', pincode: '',
    primaryEmail: '', primaryMobile: '',
    businessNature: '', nicCode: '',
    annualTurnover: '', supplyType: 'goods_services',
    signatoryName: '', signatoryPan: '', signatoryDesignation: '',
  });

  // ── Load business passport data ──────────────────────────────────────────
  useEffect(() => {
    async function loadPassport() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase
          .from('businesses')
          .select('*, founders(*), registered_offices(*), business_activities(*), business_passports(*)')
          .eq('user_id', user.id)
          .limit(1)
          .single();
        if (data) setPassport(data as BusinessPassport);
      } catch {
        // use demo data
      }
    }
    loadPassport();
  }, []);

  // ── Auto-populate form from passport ────────────────────────────────────
  useEffect(() => {
    const office = passport.registered_offices?.[0];
    const director = passport.founders?.find(f => f.is_director) || passport.founders?.[0];
    const activity = passport.business_activities?.[0];
    setForm(prev => ({
      ...prev,
      legalName: passport.legal_name || '',
      tradeName: passport.trade_name || '',
      pan: passport.pan || '',
      entityType: passport.entity_type || '',
      addressLine1: office?.address_line1 || '',
      addressLine2: office?.address_line2 || '',
      city: office?.city || '',
      state: office?.state || '',
      pincode: office?.pincode || '',
      primaryEmail: director?.email || '',
      businessNature: activity?.description || '',
      nicCode: activity?.nic_code || '',
      signatoryName: director?.full_name || '',
      signatoryPan: director?.pan || '',
      signatoryDesignation: director?.is_director ? 'Director' : 'Partner',
    }));
  }, [passport]);

  // ── Eligibility checker ──────────────────────────────────────────────────
  const checkEligibility = () => {
    setCheckingEligibility(true);
    setTimeout(() => {
      const turnoverNum = parseFloat(turnoverInput.replace(/[^0-9.]/g, '')) || 0;
      const missingFields: string[] = [];
      if (!passport.pan) missingFields.push('Business PAN');
      if (!passport.registered_offices?.length) missingFields.push('Registered Office Address');
      if (!passport.founders?.length) missingFields.push('Authorized Signatory details');
      if (!passport.business_activities?.length) missingFields.push('Business Activity / NIC Code');

      const mandatoryByTurnover = turnoverNum >= 40 || (supplyType === 'services' && turnoverNum >= 20);
      const mandatoryByInterState = interState;
      const mandatory = mandatoryByTurnover || mandatoryByInterState;

      const score = Math.max(0, 100 - missingFields.length * 20);

      setEligibility({
        eligible: missingFields.length === 0,
        mandatoryRegistration: mandatory,
        reason: mandatory
          ? interState
            ? 'Inter-state supply of goods/services mandates GST registration regardless of turnover.'
            : `Annual turnover of ₹${turnoverNum}L exceeds the ₹${supplyType === 'services' ? '20' : '40'}L threshold.`
          : `Voluntary registration — turnover below threshold. Recommended for input tax credit benefits.`,
        missingFields,
        score,
        turnoverThreshold: supplyType === 'services' ? '₹20 Lakhs' : '₹40 Lakhs',
        recommendation: missingFields.length === 0
          ? 'Your Business Passport is complete. You can proceed with GST registration.'
          : `Complete ${missingFields.length} missing field(s) in your Business Passport before filing.`,
      });
      setCheckingEligibility(false);
    }, 1200);
  };

  // ── AI guidance ──────────────────────────────────────────────────────────
  const getAIGuidance = async () => {
    setLoadingAI(true);
    try {
      const response = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'gst_guidance',
          businessData: {
            entityType: form.entityType,
            industry: passport.business_activities?.[0]?.industry,
            state: form.state,
            turnover: turnoverInput,
            supplyType,
          },
        }),
      });
      const data = await response.json();
      if (data.guidance) {
        setAiGuidance(data.guidance);
      } else {
        // Fallback guidance
        setAiGuidance([
          { field: 'Business Nature', suggestion: `For ${entityTypeLabels[form.entityType] || 'your entity'} in ${passport.business_activities?.[0]?.industry || 'your industry'}, describe your principal supply clearly — this determines your HSN/SAC code.`, confidence: 'high' },
          { field: 'HSN/SAC Code', suggestion: `Based on NIC code ${form.nicCode || 'your activity'}, your likely HSN code falls under Chapter 33 (Cosmetics/Perfumes). Verify with a CA before filing.`, confidence: 'medium' },
          { field: 'Composition Scheme', suggestion: `If your turnover is below ₹1.5 Cr and you supply only within ${form.state || 'your state'}, you may opt for the Composition Scheme (1–6% flat tax, simplified returns).`, confidence: 'high' },
          { field: 'GSTIN Format', suggestion: `Your GSTIN will be: ${stateGSTCodes[form.state] || 'XX'}${form.pan || 'XXXXXXXXXX'}1Z5 — state code + PAN + entity number + Z + check digit.`, confidence: 'high' },
        ]);
      }
    } catch {
      setAiGuidance([
        { field: 'Registration Type', suggestion: 'Regular GST registration is recommended for your entity type. It allows full input tax credit claims.', confidence: 'high' },
        { field: 'Filing Frequency', suggestion: 'As a new registrant, you will file GSTR-1 (monthly/quarterly) and GSTR-3B (monthly). Annual return GSTR-9 is due by December 31.', confidence: 'high' },
      ]);
    } finally {
      setLoadingAI(false);
    }
  };

  const updateForm = (field: keyof GSTFormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const passportScore = passport.business_passports?.completeness_score || passport.setup_progress || 0;
  const autoFilledCount = [form.legalName, form.pan, form.addressLine1, form.state, form.primaryEmail, form.businessNature, form.signatoryName].filter(Boolean).length;

  // ─── Render ──────────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/gst" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen flex items-center justify-center px-6">
          <div className="max-w-lg w-full text-center">
            <div className="w-16 h-16 bg-success-light rounded-2xl flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">✓</span>
            </div>
            <h1 className="text-2xl font-bold text-ink mb-3">Application Submitted</h1>
            <p className="text-ink-muted text-[14px] mb-2">Your GST registration application has been prepared and is ready for filing on the GST portal.</p>
            <p className="text-[13px] text-ink-subtle mb-8">ARN (Application Reference Number) will be generated within 15 minutes of portal submission. Approval typically takes 3–7 working days.</p>
            <div className="bg-white rounded-xl border border-border p-5 text-left mb-6 space-y-3">
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">Legal Name</span>
                <span className="font-medium text-ink">{form.legalName}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">PAN</span>
                <span className="font-medium text-ink">{form.pan}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">State</span>
                <span className="font-medium text-ink">{form.state} ({stateGSTCodes[form.state] || '—'})</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">Expected GSTIN</span>
                <span className="font-medium text-ai">{stateGSTCodes[form.state] || 'XX'}{form.pan || 'XXXXXXXXXX'}1Z5</span>
              </div>
            </div>
            <div className="flex gap-3 justify-center">
              <Link href="/dashboard" className="px-5 py-2.5 rounded-xl bg-surface-3 text-ink text-[13px] font-medium hover:bg-surface-2 transition-all border border-border">
                Back to Dashboard
              </Link>
              <button onClick={() => { setSubmitted(false); setStep(1); }} className="px-5 py-2.5 rounded-xl bg-ai text-white text-[13px] font-semibold hover:bg-ai-dark transition-all">
                Start New Application
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/gst" />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
        <div className="max-w-5xl mx-auto px-6 py-8">

          {/* ── Header ── */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Link href="/dashboard" className="text-ink-muted hover:text-ink text-[13px] transition-colors">Dashboard</Link>
                <span className="text-ink-subtle text-[13px]">/</span>
                <span className="text-[13px] font-medium text-ink">GST Registration</span>
              </div>
              <h1 className="text-2xl font-bold text-ink">GST Registration</h1>
              <p className="text-ink-muted text-[13px] mt-1">AI-guided workflow · Auto-filled from Business Passport</p>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide">Passport Score</p>
                <p className="text-xl font-bold text-ink">{passportScore}%</p>
              </div>
              <div className="w-10 h-10 rounded-full border-2 border-ai flex items-center justify-center">
                <span className="text-[11px] font-bold text-ai">{passportScore}%</span>
              </div>
            </div>
          </div>

          {/* ── Passport auto-fill banner ── */}
          {autoFilledCount > 0 && (
            <div className="mb-6 flex items-center gap-3 bg-ai-light border border-ai/20 rounded-xl px-4 py-3">
              <span className="text-ai text-lg">◈</span>
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-ai">{autoFilledCount} fields auto-filled from Business Passport</p>
                <p className="text-[12px] text-ai/70">Review and confirm each field before submitting.</p>
              </div>
              <Link href="/business" className="text-[12px] font-medium text-ai hover:underline flex-shrink-0">Update Passport →</Link>
            </div>
          )}

          {/* ── Tab bar ── */}
          <div className="flex gap-1 bg-white border border-border rounded-xl p-1 mb-6 w-fit">
            {(['form', 'fees', 'docs'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all capitalize ${
                  activeTab === tab ? 'bg-ai text-white shadow-sm' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {tab === 'form' ? 'Registration Form' : tab === 'fees' ? 'Fee Structure' : 'Documents Required'}
              </button>
            ))}
          </div>

          {/* ══════════════════════════════════════════════════════════════════
              TAB: REGISTRATION FORM
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'form' && (
            <div className="flex gap-6">

              {/* ── Step sidebar ── */}
              <div className="hidden md:block w-44 flex-shrink-0">
                <nav className="space-y-1 sticky top-8">
                  {workflowSteps.map(s => (
                    <button
                      key={s.id}
                      onClick={() => eligibility?.eligible !== false && setStep(s.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all text-left ${
                        step === s.id ? 'bg-ai-light text-ai' :
                        s.id < step ? 'text-success': 'text-ink-muted hover:text-ink hover:bg-surface-3'
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                        s.id < step ? 'bg-success text-white' :
                        step === s.id ? 'bg-ai text-white': 'bg-surface-3 text-ink-muted'
                      }`}>
                        {s.id < step ? '✓' : s.id}
                      </span>
                      {s.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* ── Form content ── */}
              <div className="flex-1 space-y-5">

                {/* Mobile step indicator */}
                <div className="md:hidden flex gap-1 overflow-x-auto pb-1">
                  {workflowSteps.map(s => (
                    <div key={s.id} className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      s.id < step ? 'bg-success text-white' :
                      step === s.id ? 'bg-ai text-white': 'bg-surface-3 text-ink-muted'
                    }`}>
                      {s.id < step ? '✓' : s.id}
                    </div>
                  ))}
                </div>

                {/* ── STEP 1: Eligibility ── */}
                {step === 1 && (
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl border border-border p-6">
                      <h2 className="font-semibold text-ink text-[15px] mb-1">Eligibility Checker</h2>
                      <p className="text-[13px] text-ink-muted mb-5">Check if GST registration is mandatory or voluntary for your business.</p>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Annual Turnover (in Lakhs)</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted text-[13px] font-medium">₹</span>
                            <input
                              type="number"
                              value={turnoverInput}
                              onChange={e => setTurnoverInput(e.target.value)}
                              placeholder="e.g. 25"
                              className="w-full pl-8 pr-4 py-2.5 border border-border rounded-lg text-[13px] text-ink focus:outline-none focus:border-ai bg-white"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted text-[12px]">Lakhs</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Nature of Supply</label>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { value: 'goods', label: 'Goods only' },
                              { value: 'services', label: 'Services only' },
                              { value: 'goods_services', label: 'Both' },
                            ].map(opt => (
                              <button
                                key={opt.value}
                                onClick={() => setSupplyType(opt.value)}
                                className={`py-2.5 px-3 rounded-lg border text-[12px] font-medium transition-all ${
                                  supplyType === opt.value ? 'border-ai bg-ai-light text-ai' : 'border-border text-ink-muted hover:border-ai/40'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3.5 bg-surface-2 rounded-lg border border-border">
                          <div>
                            <p className="text-[13px] font-medium text-ink">Inter-state supply?</p>
                            <p className="text-[12px] text-ink-muted">Selling to customers in other states</p>
                          </div>
                          <button
                            onClick={() => setInterState(!interState)}
                            className={`w-11 h-6 rounded-full transition-all relative ${interState ? 'bg-ai' : 'bg-surface-3 border border-border'}`}
                          >
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${interState ? 'left-5' : 'left-0.5'}`}></span>
                          </button>
                        </div>

                        <button
                          onClick={checkEligibility}
                          disabled={checkingEligibility}
                          className="w-full py-3 bg-ai text-white font-semibold rounded-xl text-[14px] hover:bg-ai-dark transition-all disabled:opacity-60"
                        >
                          {checkingEligibility ? 'Checking eligibility…' : 'Check Eligibility'}
                        </button>
                      </div>
                    </div>

                    {/* Eligibility result */}
                    {eligibility && (
                      <div className={`rounded-xl border p-5 ${eligibility.eligible ? 'bg-success-light border-success/20' : 'bg-warning/5 border-warning/20'}`}>
                        <div className="flex items-start gap-3 mb-4">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${eligibility.eligible ? 'bg-success text-white' : 'bg-warning text-white'}`}>
                            <span className="text-[14px] font-bold">{eligibility.eligible ? '✓' : '!'}</span>
                          </div>
                          <div>
                            <p className={`font-semibold text-[14px] ${eligibility.eligible ? 'text-success' : 'text-warning'}`}>
                              {eligibility.eligible ? 'Eligible to Register' : 'Complete Business Passport First'}
                            </p>
                            <p className="text-[13px] text-ink-muted mt-0.5">{eligibility.reason}</p>
                          </div>
                        </div>

                        {eligibility.mandatoryRegistration && (
                          <div className="mb-3 px-3 py-2 bg-white/60 rounded-lg border border-success/20">
                            <p className="text-[12px] font-semibold text-success">⚡ Mandatory Registration</p>
                            <p className="text-[12px] text-ink-muted">Threshold: {eligibility.turnoverThreshold} for {supplyType === 'services' ? 'service providers' : 'goods suppliers'}</p>
                          </div>
                        )}

                        {eligibility.missingFields.length > 0 && (
                          <div className="mb-3">
                            <p className="text-[12px] font-semibold text-ink-muted mb-2">Missing from Business Passport:</p>
                            <div className="space-y-1">
                              {eligibility.missingFields.map(f => (
                                <div key={f} className="flex items-center gap-2 text-[12px] text-warning">
                                  <span>○</span> {f}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <p className="text-[12px] text-ink-muted">{eligibility.recommendation}</p>

                        {eligibility.eligible && (
                          <button
                            onClick={() => setStep(2)}
                            className="mt-4 w-full py-2.5 bg-ai text-white font-semibold rounded-xl text-[13px] hover:bg-ai-dark transition-all"
                          >
                            Proceed to Registration Form →
                          </button>
                        )}
                      </div>
                    )}

                    {/* Threshold reference */}
                    <div className="bg-white rounded-xl border border-border p-5">
                      <h3 className="font-semibold text-ink text-[14px] mb-3">GST Registration Thresholds</h3>
                      <div className="space-y-2">
                        {[
                          { label: 'Goods suppliers (general states)', threshold: '₹40 Lakhs', type: 'Mandatory' },
                          { label: 'Service providers (general states)', threshold: '₹20 Lakhs', type: 'Mandatory' },
                          { label: 'Special category states (NE + hills)', threshold: '₹10 Lakhs', type: 'Mandatory' },
                          { label: 'Inter-state supply (any amount)', threshold: '₹0', type: 'Mandatory' },
                          { label: 'E-commerce operators', threshold: '₹0', type: 'Mandatory' },
                          { label: 'Voluntary registration', threshold: 'Any turnover', type: 'Optional' },
                        ].map(row => (
                          <div key={row.label} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                            <span className="text-[12px] text-ink-muted flex-1">{row.label}</span>
                            <span className="text-[12px] font-semibold text-ink mx-4">{row.threshold}</span>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${row.type === 'Mandatory' ? 'bg-danger-light text-danger' : 'bg-ai-light text-ai'}`}>
                              {row.type}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── STEP 2: Business Details ── */}
                {step === 2 && (
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl border border-border p-6">
                      <div className="flex items-center justify-between mb-5">
                        <h2 className="font-semibold text-ink text-[15px]">Business Details</h2>
                        <button
                          onClick={getAIGuidance}
                          disabled={loadingAI}
                          className="flex items-center gap-1.5 text-[12px] font-medium text-ai hover:text-ai-dark transition-colors disabled:opacity-60"
                        >
                          <span>◎</span>
                          {loadingAI ? 'Getting AI tips…' : 'Get AI Guidance'}
                        </button>
                      </div>

                      <div className="space-y-4">
                        <FormField label="Legal Name of Business" value={form.legalName} onChange={v => updateForm('legalName', v)} autoFilled={!!passport.legal_name} placeholder="As per PAN / Certificate of Incorporation" />
                        <FormField label="Trade Name (if different)" value={form.tradeName} onChange={v => updateForm('tradeName', v)} autoFilled={!!passport.trade_name} placeholder="Brand / trade name" optional />
                        <FormField label="PAN of Business" value={form.pan} onChange={v => updateForm('pan', v)} autoFilled={!!passport.pan} placeholder="AAACE1234F" hint="10-character alphanumeric PAN" />

                        <div>
                          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Entity Type</label>
                          <select
                            value={form.entityType}
                            onChange={e => updateForm('entityType', e.target.value)}
                            className="w-full px-3 py-2.5 border border-border rounded-lg text-[13px] text-ink focus:outline-none focus:border-ai bg-white"
                          >
                            <option value="">Select entity type</option>
                            {Object.entries(entityTypeLabels).map(([k, v]) => (
                              <option key={k} value={k}>{v}</option>
                            ))}
                          </select>
                          {passport.entity_type && (
                            <p className="text-[11px] text-ai mt-1">◈ Auto-filled from Business Passport</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* AI Guidance panel */}
                    {aiGuidance.length > 0 && (
                      <div className="bg-white rounded-xl border border-ai/20 p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-ai">◎</span>
                          <h3 className="font-semibold text-ink text-[14px]">AI Guidance</h3>
                          <span className="text-[10px] font-semibold bg-ai-light text-ai px-2 py-0.5 rounded-full">Informational only</span>
                        </div>
                        <div className="space-y-3">
                          {aiGuidance.map((g, i) => (
                            <div key={i} className="flex gap-3 p-3 bg-surface-2 rounded-lg">
                              <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${g.confidence === 'high' ? 'bg-success' : g.confidence === 'medium' ? 'bg-warning' : 'bg-ink-subtle'}`}></div>
                              <div>
                                <p className="text-[12px] font-semibold text-ink mb-0.5">{g.field}</p>
                                <p className="text-[12px] text-ink-muted">{g.suggestion}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-[11px] text-ink-subtle mt-3">AI guidance is informational only. Verify with a qualified CA before filing.</p>
                      </div>
                    )}

                    <StepNav onBack={() => setStep(1)} onNext={() => setStep(3)} />
                  </div>
                )}

                {/* ── STEP 3: Address & Contact ── */}
                {step === 3 && (
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl border border-border p-6">
                      <h2 className="font-semibold text-ink text-[15px] mb-5">Principal Place of Business</h2>
                      <div className="space-y-4">
                        <FormField label="Address Line 1" value={form.addressLine1} onChange={v => updateForm('addressLine1', v)} autoFilled={!!passport.registered_offices?.[0]?.address_line1} placeholder="Building, Street" />
                        <FormField label="Address Line 2" value={form.addressLine2} onChange={v => updateForm('addressLine2', v)} autoFilled={!!passport.registered_offices?.[0]?.address_line2} placeholder="Area, Locality" optional />
                        <div className="grid grid-cols-2 gap-4">
                          <FormField label="City" value={form.city} onChange={v => updateForm('city', v)} autoFilled={!!passport.registered_offices?.[0]?.city} placeholder="City" />
                          <FormField label="Pincode" value={form.pincode} onChange={v => updateForm('pincode', v)} autoFilled={!!passport.registered_offices?.[0]?.pincode} placeholder="6-digit pincode" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">State</label>
                          <select
                            value={form.state}
                            onChange={e => updateForm('state', e.target.value)}
                            className="w-full px-3 py-2.5 border border-border rounded-lg text-[13px] text-ink focus:outline-none focus:border-ai bg-white"
                          >
                            <option value="">Select state</option>
                            {Object.keys(stateGSTCodes).map(s => (
                              <option key={s} value={s}>{s} ({stateGSTCodes[s]})</option>
                            ))}
                          </select>
                          {form.state && <p className="text-[11px] text-ai mt-1">◈ GST State Code: {stateGSTCodes[form.state]}</p>}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-border p-6">
                      <h2 className="font-semibold text-ink text-[15px] mb-5">Contact Details</h2>
                      <div className="space-y-4">
                        <FormField label="Primary Email" value={form.primaryEmail} onChange={v => updateForm('primaryEmail', v)} autoFilled={!!passport.founders?.[0]?.email} placeholder="email@company.com" hint="OTP will be sent for verification" />
                        <FormField label="Primary Mobile" value={form.primaryMobile} onChange={v => updateForm('primaryMobile', v)} placeholder="+91 XXXXX XXXXX" hint="10-digit mobile number" />
                      </div>
                    </div>

                    <StepNav onBack={() => setStep(2)} onNext={() => setStep(4)} />
                  </div>
                )}

                {/* ── STEP 4: Business Activity ── */}
                {step === 4 && (
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl border border-border p-6">
                      <h2 className="font-semibold text-ink text-[15px] mb-5">Business Activity</h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Nature of Business</label>
                          <textarea
                            value={form.businessNature}
                            onChange={e => updateForm('businessNature', e.target.value)}
                            rows={3}
                            placeholder="Describe your principal business activity…"
                            className="w-full px-3 py-2.5 border border-border rounded-lg text-[13px] text-ink focus:outline-none focus:border-ai bg-white resize-none"
                          />
                          {passport.business_activities?.[0] && (
                            <p className="text-[11px] text-ai mt-1">◈ Auto-filled from Business Passport</p>
                          )}
                        </div>
                        <FormField label="NIC Code" value={form.nicCode} onChange={v => updateForm('nicCode', v)} autoFilled={!!passport.business_activities?.[0]?.nic_code} placeholder="e.g. 20231" hint="National Industrial Classification code" />

                        <div>
                          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Type of Supply</label>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { value: 'goods', label: 'Goods' },
                              { value: 'services', label: 'Services' },
                              { value: 'goods_services', label: 'Both' },
                            ].map(opt => (
                              <button
                                key={opt.value}
                                onClick={() => updateForm('supplyType', opt.value)}
                                className={`py-2.5 rounded-lg border text-[12px] font-medium transition-all ${
                                  form.supplyType === opt.value ? 'border-ai bg-ai-light text-ai' : 'border-border text-ink-muted hover:border-ai/40'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Expected Annual Turnover</label>
                          <select
                            value={form.annualTurnover}
                            onChange={e => updateForm('annualTurnover', e.target.value)}
                            className="w-full px-3 py-2.5 border border-border rounded-lg text-[13px] text-ink focus:outline-none focus:border-ai bg-white"
                          >
                            <option value="">Select range</option>
                            <option value="below_20">Below ₹20 Lakhs</option>
                            <option value="20_40">₹20 – ₹40 Lakhs</option>
                            <option value="40_75">₹40 – ₹75 Lakhs</option>
                            <option value="75_150">₹75 Lakhs – ₹1.5 Crore</option>
                            <option value="above_150">Above ₹1.5 Crore</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <StepNav onBack={() => setStep(3)} onNext={() => setStep(5)} />
                  </div>
                )}

                {/* ── STEP 5: Authorized Signatory ── */}
                {step === 5 && (
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl border border-border p-6">
                      <h2 className="font-semibold text-ink text-[15px] mb-1">Authorized Signatory</h2>
                      <p className="text-[13px] text-ink-muted mb-5">The person authorized to sign and submit the GST application.</p>

                      {passport.founders?.length > 0 && (
                        <div className="mb-5">
                          <p className="text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Select from Business Passport</p>
                          <div className="space-y-2">
                            {passport.founders.map((f, i) => (
                              <button
                                key={i}
                                onClick={() => setForm(prev => ({ ...prev, signatoryName: f.full_name, signatoryPan: f.pan, signatoryDesignation: f.is_director ? 'Director' : 'Partner', primaryEmail: f.email }))}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                                  form.signatoryName === f.full_name ? 'border-ai bg-ai-light' : 'border-border hover:border-ai/30'
                                }`}
                              >
                                <div className="w-8 h-8 bg-ai-light rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-ai font-semibold text-[12px]">{f.full_name.charAt(0)}</span>
                                </div>
                                <div className="flex-1">
                                  <p className="text-[13px] font-medium text-ink">{f.full_name}</p>
                                  <p className="text-[11px] text-ink-muted">{f.email} · PAN: {f.pan}</p>
                                </div>
                                {f.is_director && <span className="text-[10px] font-semibold bg-ai-light text-ai px-2 py-0.5 rounded-full border border-ai/20">Director</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="space-y-4">
                        <FormField label="Full Name" value={form.signatoryName} onChange={v => updateForm('signatoryName', v)} autoFilled={!!form.signatoryName} placeholder="As per Aadhaar / PAN" />
                        <FormField label="PAN" value={form.signatoryPan} onChange={v => updateForm('signatoryPan', v)} autoFilled={!!form.signatoryPan} placeholder="ABCDE1234F" />
                        <div>
                          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Designation</label>
                          <select
                            value={form.signatoryDesignation}
                            onChange={e => updateForm('signatoryDesignation', e.target.value)}
                            className="w-full px-3 py-2.5 border border-border rounded-lg text-[13px] text-ink focus:outline-none focus:border-ai bg-white"
                          >
                            <option value="">Select designation</option>
                            <option value="Director">Director</option>
                            <option value="Managing Director">Managing Director</option>
                            <option value="Partner">Partner</option>
                            <option value="Proprietor">Proprietor</option>
                            <option value="Authorized Signatory">Authorized Signatory</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <StepNav onBack={() => setStep(4)} onNext={() => setStep(6)} nextLabel="Review Application →" />
                  </div>
                )}

                {/* ── STEP 6: Review & Submit ── */}
                {step === 6 && (
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl border border-border p-6">
                      <h2 className="font-semibold text-ink text-[15px] mb-5">Review Application</h2>

                      <div className="space-y-5">
                        <ReviewSection title="Business Details" items={[
                          { label: 'Legal Name', value: form.legalName },
                          { label: 'Trade Name', value: form.tradeName },
                          { label: 'PAN', value: form.pan },
                          { label: 'Entity Type', value: entityTypeLabels[form.entityType] || form.entityType },
                        ]} onEdit={() => setStep(2)} />

                        <ReviewSection title="Address & Contact" items={[
                          { label: 'Address', value: [form.addressLine1, form.addressLine2, form.city, form.state, form.pincode].filter(Boolean).join(', ') },
                          { label: 'GST State Code', value: stateGSTCodes[form.state] || '—' },
                          { label: 'Email', value: form.primaryEmail },
                          { label: 'Mobile', value: form.primaryMobile },
                        ]} onEdit={() => setStep(3)} />

                        <ReviewSection title="Business Activity" items={[
                          { label: 'Nature', value: form.businessNature },
                          { label: 'NIC Code', value: form.nicCode },
                          { label: 'Supply Type', value: form.supplyType },
                          { label: 'Turnover Range', value: form.annualTurnover },
                        ]} onEdit={() => setStep(4)} />

                        <ReviewSection title="Authorized Signatory" items={[
                          { label: 'Name', value: form.signatoryName },
                          { label: 'PAN', value: form.signatoryPan },
                          { label: 'Designation', value: form.signatoryDesignation },
                        ]} onEdit={() => setStep(5)} />
                      </div>

                      {/* Expected GSTIN */}
                      {form.pan && form.state && (
                        <div className="mt-5 p-4 bg-ai-light rounded-xl border border-ai/20">
                          <p className="text-[11px] font-semibold text-ai uppercase tracking-wide mb-1">Expected GSTIN Format</p>
                          <p className="text-[18px] font-bold text-ink tracking-widest">
                            {stateGSTCodes[form.state]}{form.pan}1Z5
                          </p>
                          <p className="text-[11px] text-ai/70 mt-1">State Code ({stateGSTCodes[form.state]}) + PAN + Entity No. + Z + Check Digit</p>
                        </div>
                      )}
                    </div>

                    <div className="bg-white rounded-xl border border-border p-5">
                      <p className="text-[12px] text-ink-muted">
                        <span className="font-semibold text-ink">Disclaimer:</span> This application is prepared using AI-assisted form filling based on your Business Passport data. 
                        All information must be verified before submission on the official GST portal (gst.gov.in). 
                        BusinessOS is not responsible for errors in the final filing.
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <button onClick={() => setStep(5)} className="flex-1 py-3 border border-border rounded-xl text-[13px] font-medium text-ink-muted hover:text-ink hover:bg-surface-2 transition-all">
                        ← Back
                      </button>
                      <button
                        onClick={() => setSubmitted(true)}
                        className="flex-1 py-3 bg-ai text-white font-semibold rounded-xl text-[14px] hover:bg-ai-dark transition-all"
                      >
                        Submit Application →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              TAB: FEE STRUCTURE
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'fees' && (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="font-semibold text-ink text-[15px] mb-1">Government Fee Structure</h2>
                <p className="text-[13px] text-ink-muted mb-5">GST registration itself is free. Costs arise from supporting requirements.</p>

                <div className="space-y-3">
                  {feeStructure.map((fee, i) => (
                    <div key={i} className={`flex items-start gap-4 p-4 rounded-xl border ${
                      fee.type === 'free' ? 'bg-success-light border-success/20' :
                      fee.type === 'required'? 'bg-white border-border' : 'bg-surface-2 border-border'
                    }`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        fee.type === 'free' ? 'bg-success text-white' :
                        fee.type === 'required'? 'bg-ai-light text-ai' : 'bg-surface-3 text-ink-muted'
                      }`}>
                        <span className="text-[12px] font-bold">{fee.type === 'free' ? '₹0' : fee.type === 'required' ? '!' : '?'}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[13px] font-semibold text-ink">{fee.category}</p>
                          <span className={`text-[13px] font-bold ${fee.type === 'free' ? 'text-success' : 'text-ink'}`}>{fee.amount}</span>
                        </div>
                        <p className="text-[12px] text-ink-muted">{fee.note}</p>
                        <span className={`inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                          fee.type === 'free' ? 'bg-success/10 text-success' :
                          fee.type === 'required'? 'bg-ai-light text-ai' : 'bg-surface-3 text-ink-muted'
                        }`}>
                          {fee.type === 'free' ? 'FREE' : fee.type === 'required' ? 'REQUIRED' : 'OPTIONAL'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total estimate */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h3 className="font-semibold text-ink text-[14px] mb-4">Estimated Total Cost</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-ink-muted">Government fee</span>
                    <span className="font-semibold text-success">₹0</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-ink-muted">DSC (if applicable)</span>
                    <span className="font-medium text-ink">₹1,000 – ₹2,500</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-ink-muted">Professional fee (optional)</span>
                    <span className="font-medium text-ink">₹1,500 – ₹5,000</span>
                  </div>
                  <div className="border-t border-border pt-2 flex justify-between text-[14px]">
                    <span className="font-semibold text-ink">Total (estimated)</span>
                    <span className="font-bold text-ink">₹0 – ₹7,500</span>
                  </div>
                </div>
                <p className="text-[11px] text-ink-subtle">Costs vary based on entity type and whether professional assistance is used. DSC is mandatory for companies and LLPs.</p>
              </div>

              {/* GST rates reference */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h3 className="font-semibold text-ink text-[14px] mb-4">GST Rate Slabs (Reference)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { rate: '0%', desc: 'Essential goods: food grains, milk, eggs, fresh vegetables', color: 'bg-success-light text-success' },
                    { rate: '5%', desc: 'Basic necessities: packaged food, medicines, transport', color: 'bg-ai-light text-ai' },
                    { rate: '12%', desc: 'Processed food, computers, business class air travel', color: 'bg-warning/10 text-warning' },
                    { rate: '18%', desc: 'Most goods & services: electronics, restaurants, telecom', color: 'bg-surface-3 text-ink-muted' },
                    { rate: '28%', desc: 'Luxury items: cars, tobacco, aerated drinks, casinos', color: 'bg-danger-light text-danger' },
                  ].map(slab => (
                    <div key={slab.rate} className={`p-3 rounded-xl ${slab.color.split(' ')[0]} border border-current/10`}>
                      <p className={`text-2xl font-bold mb-1 ${slab.color.split(' ')[1]}`}>{slab.rate}</p>
                      <p className="text-[11px] text-ink-muted">{slab.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              TAB: DOCUMENTS REQUIRED
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'docs' && (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="font-semibold text-ink text-[15px] mb-1">Documents Required</h2>
                <p className="text-[13px] text-ink-muted mb-5">
                  Based on your entity type: <span className="font-semibold text-ink">{entityTypeLabels[passport.entity_type] || 'Your Business'}</span>
                </p>

                <div className="space-y-3">
                  {requiredDocuments.map((doc, i) => {
                    const isApplicable = doc.who === 'All entity types' ||
                      (doc.who.includes('Companies') && ['private_limited', 'opc'].includes(passport.entity_type)) ||
                      (doc.who.includes('LLPs') && passport.entity_type === 'llp') ||
                      (doc.who.includes('Partnership') && passport.entity_type === 'partnership');
                    return (
                      <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border ${isApplicable ? 'bg-white border-border' : 'bg-surface-2 border-border opacity-50'}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          doc.status === 'required' ? 'bg-ai text-white' : 'bg-warning/20 text-warning'
                        }`}>
                          <span className="text-[10px] font-bold">{doc.status === 'required' ? '!' : '?'}</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-[13px] font-medium text-ink">{doc.doc}</p>
                          <p className="text-[11px] text-ink-muted mt-0.5">Applicable for: {doc.who}</p>
                        </div>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          doc.status === 'required' ? 'bg-ai-light text-ai' : 'bg-warning/10 text-warning'
                        }`}>
                          {doc.status === 'required' ? 'REQUIRED' : 'IF APPLICABLE'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-ai-light rounded-xl border border-ai/20 p-5">
                <div className="flex items-start gap-3">
                  <span className="text-ai text-lg mt-0.5">◈</span>
                  <div>
                    <p className="text-[13px] font-semibold text-ai mb-1">Business Passport Integration</p>
                    <p className="text-[13px] text-ink-muted">
                      Your Business Passport already contains {autoFilledCount} of the required data points. 
                      Upload remaining documents in the <Link href="/documents" className="text-ai font-medium hover:underline">Document Center</Link> to complete your profile.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-border p-6">
                <h3 className="font-semibold text-ink text-[14px] mb-4">Timeline</h3>
                <div className="space-y-0">
                  {[
                    { phase: 'Document preparation', duration: '1–2 days', note: 'Gather all required documents' },
                    { phase: 'Application filing on GST portal', duration: '1 day', note: 'Submit at gst.gov.in' },
                    { phase: 'ARN generation', duration: '15 minutes', note: 'Automatic after submission' },
                    { phase: 'Officer review', duration: '3–7 working days', note: 'GST officer may raise queries' },
                    { phase: 'GSTIN issued', duration: 'Day 7–10', note: 'Certificate available on portal' },
                  ].map((t, i, arr) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-7 h-7 rounded-full bg-ai-light border-2 border-ai flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-ai">{i + 1}</span>
                        </div>
                        {i < arr.length - 1 && <div className="w-0.5 h-8 bg-border mt-1"></div>}
                      </div>
                      <div className="pb-6">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-[13px] font-medium text-ink">{t.phase}</p>
                          <span className="text-[11px] font-semibold text-ai bg-ai-light px-2 py-0.5 rounded-full">{t.duration}</span>
                        </div>
                        <p className="text-[12px] text-ink-muted">{t.note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FormField({
  label, value, onChange, placeholder, autoFilled, hint, optional,
}: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; autoFilled?: boolean; hint?: string; optional?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <label className="text-[12px] font-semibold text-ink-muted uppercase tracking-wide">{label}</label>
        {optional && <span className="text-[10px] text-ink-subtle">(optional)</span>}
        {autoFilled && <span className="text-[10px] font-semibold text-ai bg-ai-light px-1.5 py-0.5 rounded">◈ Auto-filled</span>}
      </div>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2.5 border rounded-lg text-[13px] text-ink focus:outline-none focus:border-ai bg-white transition-colors ${
          autoFilled ? 'border-ai/30 bg-ai-light/20' : 'border-border'
        }`}
      />
      {hint && <p className="text-[11px] text-ink-subtle mt-1">{hint}</p>}
    </div>
  );
}

function StepNav({ onBack, onNext, nextLabel = 'Continue →' }: { onBack: () => void; onNext: () => void; nextLabel?: string }) {
  return (
    <div className="flex gap-3">
      <button onClick={onBack} className="flex-1 py-3 border border-border rounded-xl text-[13px] font-medium text-ink-muted hover:text-ink hover:bg-surface-2 transition-all">
        ← Back
      </button>
      <button onClick={onNext} className="flex-1 py-3 bg-ai text-white font-semibold rounded-xl text-[14px] hover:bg-ai-dark transition-all">
        {nextLabel}
      </button>
    </div>
  );
}

function ReviewSection({ title, items, onEdit }: { title: string; items: Array<{ label: string; value: string }>; onEdit: () => void }) {
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-surface-2 border-b border-border">
        <p className="text-[12px] font-semibold text-ink-muted uppercase tracking-wide">{title}</p>
        <button onClick={onEdit} className="text-[12px] font-medium text-ai hover:text-ai-dark transition-colors">Edit</button>
      </div>
      <div className="divide-y divide-border">
        {items.map(item => (
          <div key={item.label} className="flex items-start justify-between px-4 py-3">
            <span className="text-[12px] text-ink-muted w-36 flex-shrink-0">{item.label}</span>
            <span className="text-[13px] font-medium text-ink text-right flex-1">{item.value || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
